// ---------------------------------------------------------------------------
// Lebensdaten (PF 15): Geburt und Tod einer Figur sind Änderungen ihrer Verfassung („geboren“, „tot“) in einer Szene – meist in einem
// Hintergrundereignis, das passiert, aber nie erzählt wird („Maras Geburt“). Mit der Weltzeit der Szenen ergibt sich daraus das Alter zu
// jeder Szene. Grundlage für Prüfungen wie „Auftritt vor der Geburt“ (PF 23).
// ---------------------------------------------------------------------------

import { monthsInYear, msToParts } from './calendar.js';
import type { StoryCtx } from './ctx.js';
import { worldOrder, worldSpanOf, type WorldSpan } from './narrative.js';
import { isScene } from './scene.js';
import { priorLabel, priorOf } from './series.js';
import { stateEvents, type StateEvent } from './states.js';
import type { PriorLife, PriorSource, StoryNode } from './types.js';

/** Geburt bzw. Tod: die Szene, ihre Stelle in Weltzeit und ihre Zeit */
export interface LifeEvent {
  sceneId: string;
  /** „Kapitel.Szene“, leer wenn nicht eingeordnet (Hintergrundereignis) */
  label: string;
  title: string;
  world: number;
  span: WorldSpan | null;
  /** die Zeit ist nur ungefähr (ungefähr markiert oder nur aufs Jahr genau) */
  approx: boolean;
  /** in einer früheren Episode der Serie (series.ts): dann ohne Szene in dieser Geschichte (`sceneId` leer, Weltzeit -1) */
  prior?: PriorSource;
}

export interface Life {
  born: LifeEvent | null;
  died: LifeEvent | null;
}

/** Alter in vollen Jahren, Monaten und Tagen (jeweils gesamt) */
export interface Age {
  years: number;
  months: number;
  days: number;
  approx: boolean;
}

/** Wie alt eine Figur zu einer Szene ist: noch nicht geboren, lebend (mit Alter, falls die Zeiten vergleichbar sind) oder schon tot */
export type AgeAt =
  | { state: 'unborn'; born: LifeEvent }
  | { state: 'alive'; age: Age | null; born: LifeEvent }
  | { state: 'dead'; age: Age | null; born: LifeEvent | null; died: LifeEvent };

const rough = (n: StoryNode | undefined): boolean => !!n?.time?.approx || n?.time?.precision === 'year';

function lifeEvent(ctx: StoryCtx, e: Omit<StateEvent, 'role'>): LifeEvent {
  const scene = ctx.nodes.get(e.sceneId)!;
  return { sceneId: e.sceneId, label: e.label, title: e.title, world: e.world, span: worldSpanOf(ctx, scene), approx: rough(scene) };
}

/** Geburt bzw. Tod aus einer früheren Episode */
const priorLife = (p: PriorLife): LifeEvent => ({ sceneId: '', label: priorLabel(p.episode), title: p.scene, world: -1, span: p.span, approx: p.approx, prior: { episode: p.episode, scene: p.scene } });

/** Geburt und Tod eines Elements: die erste Änderung „geboren“ bzw. „tot“ in Weltzeit – in einer Serie zuerst die früheren Episoden */
export function lifeOf(ctx: StoryCtx, entityId: string, all = stateEvents(ctx)): Life {
  const prior = priorOf(ctx, entityId);
  const first = (value: string): LifeEvent | null => {
    const e = all.find((x) => x.change.entityId === entityId && x.change.kind === 'verfassung' && x.change.value === value);
    return e ? lifeEvent(ctx, e) : null;
  };
  return { born: prior?.born ? priorLife(prior.born) : first('geboren'), died: prior?.died ? priorLife(prior.died) : first('tot') };
}

/**
 * Liegt die Szene nach einem Tod in einer früheren Episode? `sure`: beide haben vergleichbare Zeiten und sie beginnt danach; `likely`:
 * ohne vergleichbare Zeit nach der Reihenfolge der Episoden; null: sie liegt davor (eine Rückblende) – dann lebt die Figur dort noch.
 */
export function afterPriorEvent(ctx: StoryCtx, scene: StoryNode, e: LifeEvent): 'sure' | 'likely' | null {
  const at = worldSpanOf(ctx, scene);
  if (!at || !e.span || at.basis !== e.span.basis) return 'likely';
  return at.end < e.span.start ? null : 'sure';
}

const DAY = 86_400_000;

/**
 * Alter von `from` bis `to` (Beginn der Spannen), nur bei derselben Zeitrechnung; null sonst oder wenn `to` davor liegt. Datum: nach dem
 * Kalender der Geschichte (volle Jahre und Monate); eigene Zählung ohne Nullpunkt: Jahre zu 365 Tagen, Monate zu 30.
 */
export function ageBetween(from: WorldSpan, to: WorldSpan, approx = false): Age | null {
  if (from.basis !== to.basis || (from.basis !== 'ms' && from.basis !== 'count') || to.start < from.start) return null;
  if (from.basis === 'count') {
    const days = Math.floor((to.start - from.start) / 1440);
    return { years: Math.floor(days / 365), months: Math.floor(days / 30), days, approx };
  }
  // nach dem Kalender der Geschichte (calendar.ts): volle Monate, wenn Tag und Uhrzeit im Monat erreicht sind
  const a = msToParts(from.start);
  const b = msToParts(to.start);
  let months = (b.y - a.y) * monthsInYear() + (b.mo - a.mo);
  const inMonth = (p: typeof a): number => ((p.d - 1) * 24 + p.h) * 60 + p.mi;
  if (inMonth(b) < inMonth(a)) months -= 1;
  return { years: Math.floor(months / monthsInYear()), months, days: Math.floor((to.start - from.start) / DAY), approx };
}

/** Wie alt `entityId` zur Szene `sceneId` ist; null, wenn weder Geburt noch (davor) Tod bekannt sind. Die Reihenfolge gibt die Weltzeit. */
export function ageAt(ctx: StoryCtx, entityId: string, sceneId: string, all = stateEvents(ctx)): AgeAt | null {
  const scene = ctx.nodes.get(sceneId);
  if (!scene || !isScene(scene)) return null;
  const { born, died } = lifeOf(ctx, entityId, all);
  if (!born && !died) return null;
  const world = all.find((e) => e.sceneId === sceneId)?.world ?? worldOrder(ctx, ctx.story.nodes.filter(isScene)).findIndex((s) => s.id === sceneId);
  const age = (until: { span: WorldSpan | null; approx: boolean }): Age | null =>
    born?.span && until.span ? ageBetween(born.span, until.span, born.approx || until.approx) : null;
  if (died && world > died.world && (!died.prior || afterPriorEvent(ctx, scene, died))) return { state: 'dead', age: age(died), born, died };
  if (!born) return null;
  if (world < born.world) return { state: 'unborn', born };
  return { state: 'alive', age: age({ span: worldSpanOf(ctx, scene), approx: rough(scene) }), born };
}
